import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';
import { useState } from 'react';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import TaxExtensionsBulkUploadTemplate from '@/components/ui/TaxExtensionBulkUploadTemplate';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form7004Instructions = () => {
  const [isBulkUploadTemplate, setIsBulkUploadTemplate] = useState(false);

  return (
    <AppLayout
      meta={
        <Meta
          title="Form 7004 Bulk Upload | File Business Tax Extension in bulk"
          description="Learn how to file multiple business tax extensions in minutes using TaxZerone's Form 7004 Bulk Upload. Upload Excel file, validate data, and transmit returns."
          keywords=""
          canonical="resources/tax-extension/form-7004-bulk-upload/"
          ogType="article"
          jsonLdPricing="17.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <TaxExtensionsBulkUploadTemplate
        isBulkUploadTemplate={isBulkUploadTemplate}
        setIsBulkUploadTemplate={setIsBulkUploadTemplate}
        formType="7004"
      />
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 7004{' '}
          <span className="highlight-container">
            <span className="highlight"> Bulk Upload</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
            maxWidth: { xs: '100%', sm: '90%', md: '80%' },
            mx: 'auto',
          }}
        >
          TaxZerone’s Bulk Upload feature allows businesses to file multiple
          Form 7004 extensions in a few simple steps — it’s quick, easy, and
          saves time.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004bulk`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-7004-bulk-upload/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-25T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 7004 Bulk Upload at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          Form 7004 Bulk Upload
                        </span>{' '}
                        allows multiple IRS Form 7004 extension requests to be
                        uploaded, validated, and e-filed in a{' '}
                        <span style={{ fontWeight: 600 }}>
                          single submission using a preformatted Excel template
                        </span>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        The feature is designed for{' '}
                        <span style={{ fontWeight: 600 }}>
                          tax professionals, CPAs, enrolled agents, reporting
                          agents, bookkeeping firms, payroll providers, and
                          businesses
                        </span>{' '}
                        managing multiple extension filings.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        TaxZerone accepts{' '}
                        <span style={{ fontWeight: 600 }}>
                          Excel (.xlsx) files
                        </span>{' '}
                        for bulk uploads, with{' '}
                        <span style={{ fontWeight: 600 }}>
                          built-in validation
                        </span>{' '}
                        to identify errors before the filing process begins.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        Bulk Upload helps{' '}
                        <span style={{ fontWeight: 600 }}>
                          reduce manual data entry, improve filing accuracy,
                          save time,
                        </span>{' '}
                        and simplify the management of multiple Form 7004
                        extension requests.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Managing tax extensions individually for multiple clients can
                  be time-consuming and causes error. With{' '}
                  <span style={{ fontWeight: 600 }}>
                    TaxZerone’s Form 7004 Bulk Upload
                  </span>
                  , you can make the entire process smoother by uploading,
                  validating, and e-filing multiple business tax extensions in
                  one go.
                </Typography>

                <Box sx={{ pt: 2 }}>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 7004 Bulk Upload?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whocan" sx={{ color: '#0202ff' }}>
                          Who can use Bulk Upload?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#file" sx={{ color: '#0202ff' }}>
                          What file formats will be accepted?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#steps" sx={{ color: '#0202ff' }}>
                          Steps to Bulk Upload Form 7004 with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#benefits" sx={{ color: '#0202ff' }}>
                          Advantages of Form 7004 Bulk Upload with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is Form 7004 Bulk Upload?
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Bulk Upload is a feature offered by TaxZerone that allows
                    you to file multiple Form 7004 extensions at once by
                    uploading a{' '}
                    <span style={{ fontWeight: 600 }}>single Excel file</span>.
                  </li>
                  <li>
                    Instead of entering each client’s information manually, you
                    can prepare all records in one file and submit them
                    together. This will reduce manual work and improve
                    efficiency for businesses.
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { md: 17, sm: 17, xs: 16 },
                          fontWeight: 600,
                        }}
                      >
                        Still Filing 7004 Extensions One by One?
                      </Typography>
                      <Typography variant="body1">
                        Upload your Form 7004 data and submit multiple filings
                        in just a few clicks.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004bulk`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whocan"
                >
                  Who can use Bulk Upload?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Bulk Upload can be used by:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>Tax professionals, Tax Preparers / Tax Consultants</li>

                  <li>Enrolled Agents (EAs), EROs, and Reporting Agents</li>

                  <li>CPA firms and accounting teams</li>

                  <li>Tax E-File Service Providers</li>

                  <li>
                    Businesses and Bookkeeping firms managing multiple entities
                  </li>

                  <li>Payroll and compliance service providers</li>
                </Box>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  If you are filing more than a few extensions, bulk upload can
                  save time and effort.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="file"
                >
                  What file formats will be accepted?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone accepts{' '}
                  <span style={{ fontWeight: 600 }}>Excel (.xlsx)</span>file
                  format for bulk uploads. You can either upload your Excel file
                  directly, or if your file is in a different format, you can
                  use the{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}myaccount/secure-file-upload/`}
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Secure File Upload
                  </Link>{' '}
                  —our automatic file converter will generate your file in the
                  required format.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { md: 17, sm: 17, xs: 16 },
                          fontWeight: 600,
                        }}
                      >
                        Form 7004 Bulk Upload Template
                      </Typography>
                      <Typography variant="body1">
                        Skip the manual entry. Use our preformatted Excel
                        template to file for multiple clients in minutes — just
                        fill and upload.
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        ✓ Free to use ✓ Minimizes errors ✓ Saves time
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        onClick={() => {
                          setIsBulkUploadTemplate(true);
                        }}
                      >
                        Download Template
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="steps"
                >
                  Steps to Bulk Upload Form 7004 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Here are the steps to bulk upload your{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 7004 online
                  </Link>{' '}
                  with TaxZerone.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signin/?utm_source=resource&utm_campaign=form+7004+bulk+upload`}
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Sign in
                  </Link>{' '}
                  to TaxZerone account using your credentials. From the
                  dashboard, click{' '}
                  <span style={{ fontWeight: 600 }}>“Start”</span> on the right
                  side and select{' '}
                  <span style={{ fontWeight: 600 }}> “New Form” </span>from the
                  dropdown menu to proceed.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/7004-bulk-upload-dashboard-img.webp" />
                  <source srcSet="/assets/images/7004-bulk-upload-dashboard-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-bulk-upload-dashboard-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={350}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In the{' '}
                  <span style={{ fontWeight: 600 }}>Extension Forms</span>{' '}
                  section, select{' '}
                  <span style={{ fontWeight: 600 }}>Form 7004 Bulk Upload</span>{' '}
                  to begin your filing process.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/7004-bulk-started-img.webp" />
                  <source srcSet="/assets/images/7004-bulk-started-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-bulk-started-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={450}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Download the Form 7004 bulk upload template in Excel format.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/7004-bulk-img.webp" />
                  <source srcSet="/assets/images/7004-bulk-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-bulk-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={570}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the required details, including EIN, business name,
                  complete address, and other relevant information and save your
                  file.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/7004-bulk-template-img.webp" />
                  <source srcSet="/assets/images/7004-bulk-template-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-bulk-template-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={320}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 5:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Browse your file and select the completed Form 7004 Excel
                    file. The maximum file size should be{' '}
                    <span style={{ fontWeight: 600 }}> 3 MB or less</span>.{' '}
                  </li>
                  <li>
                    {' '}
                    Then click on the{' '}
                    <span style={{ fontWeight: 600 }}>“Upload”</span> button to
                    process and prepare your filings automatically.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/7004-bulk-upload-img.webp" />
                  <source srcSet="/assets/images/7004-bulk-upload-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-bulk-upload-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={575}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 6:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Verify your uploaded details to ensure they match the
                  information displayed in the table and then click{' '}
                  <span style={{ fontWeight: 600 }}>“Process File”.</span>
                </Typography>
                <picture>
                  <source srcSet="/assets/images/7004-process-file-img.webp" />
                  <source srcSet="/assets/images/7004-process-file-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-process-file-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={360}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 7:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    View the result of your bulk Form 7004 submission. Check
                    whether your records have any errors and correct them if
                    any.
                  </li>
                  <li>
                    {' '}
                    If all the records are uploaded successfully, click on{' '}
                    <span style={{ fontWeight: 600 }}>
                      ‘Go to Dashboard’
                    </span>{' '}
                    to see your returns and proceed with the filing.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/7004-success-file-img.webp" />
                  <source srcSet="/assets/images/7004-success-file-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-success-file-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={400}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 8:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Now, click on{' '}
                  <span style={{ fontWeight: 600 }}>“Continue Filing”</span> to
                  proceed with the submission of each filing individually.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/7004-continue-filing-img.webp" />
                  <source srcSet="/assets/images/7004-continue-filing-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-continue-filing-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={200}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 9:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Now, your 7004 form details will be auto-filled—review the
                    fields to ensure all information is accurate.
                  </li>
                  <li>
                    Then click on{' '}
                    <span style={{ fontWeight: 600 }}>“Save & Continue”.</span>
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/7004-auto-filled-img.webp" />
                  <source srcSet="/assets/images/7004-auto-filled-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-auto-filled-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={609}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 10:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Select the checkbox to redeem your credits to{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004bulk`}
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      e-file Form 7004
                    </Link>{' '}
                    and submit your business tax extension quickly.
                  </li>
                  <li>
                    {' '}
                    Choose any{' '}
                    <span style={{ fontWeight: 600 }}>Add-On services </span>to
                    enhance your filing experience. .
                  </li>
                  <li>
                    {' '}
                    Then, click on{' '}
                    <span style={{ fontWeight: 600 }}>
                      “E-File with IRS"{' '}
                    </span>{' '}
                    to transmit your 7004 returns.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-file Form 7004 for FREE when you prepay for your required
                  business tax return (
                  <Link
                    href="/resources/business-tax/form-1120-s-instructions/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    1120-s
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/resources/business-tax/form-1065-instructions/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    1065
                  </Link>{' '}
                  ) with TaxZerone.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="maximum-shareholders-note"
                >
                  <b>Note :</b> Refer to our support article for step-by-step
                  instructions on{' '}
                  <Link
                    href="https://support.taxzerone.com/how-to-buy-extension-forms-credits-with-taxzerone/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    purchasing extension Form credits with TaxZerone
                  </Link>
                  .
                </Alert>
                <picture>
                  <source srcSet="/assets/images/7004-checkout-img.webp" />
                  <source srcSet="/assets/images/7004-checkout-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/7004-checkout-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={380}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="benefits"
                >
                  Advantages of Form 7004 Bulk Upload with TaxZerone
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Save Time:</span> Time
                    savings are a major benefit. File multiple{' '}
                    <Link
                      href="/resources/tax-extension/form-7004-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 7004
                    </Link>{' '}
                    extensions at once instead of handling each filing
                    individually.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>Reduce Manual Work:</span>{' '}
                    There is no need to manually enter information. Just upload
                    all the information in a single Excel file.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>Minimize Errors:</span>{' '}
                    Errors will be validated and corrected before submission.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>Faster Processing:</span>{' '}
                    You can quickly create and file your extensions in a few
                    easy steps.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Improved Productivity:
                    </span>{' '}
                    You will be able to increase your productivity by focusing
                    on more important tasks.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Secure &amp; Compliant:
                    </span>{' '}
                    Your information is safe and IRS compliant.
                  </li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to Simplify Your Form 7004 Filing?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                maxWidth: { xs: '100%', md: '60%' },
                mx: 'auto',
              }}
            >
              Upload a single Excel file to streamline your process, minimize
              mistakes, and effortlessly handle multiple extensions using
              TaxZerone
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004bulk`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form7004Instructions;
